import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PolicyInfo, PolicyScope, Role } from '@up2d8/contracts';
import { LoadingState, useToast } from '@up2d8/ui';
import {
  AdminIcon,
  AdminPage,
  Btn,
  Card,
  CardHead,
  CardPad,
  Empty,
  Field,
  Grid,
  Input,
  Note,
  PageHead,
  SearchField,
  Segmented,
  Stack,
  Toggle,
  Toolbar,
} from '../admin-kit';
import { FilterMenu } from '../organisatie/filter-menu';
import { localized } from '../../shared/format';
import { useSession } from '../../shared/session';
import { ListError } from '../../shared/list-error';
import { roleName } from './roles-page';

/** In de volgorde van het segment in O7. */
const SCOPES: PolicyScope[] = ['tenant', 'own_team', 'managed_groups', 'site'];

/**
 * De rechten waarvoor een bereik iets uitmaakt: de server leest daar de scope van de toekenning.
 *
 * De catalogus (`GET /policies`) zegt niet welke dat zijn, en de server aanvaardt een scope op elk
 * recht - maar een keuze tonen die niets doet, zou liegen. Dit is de lijst van de modules die
 * `getPolicyScope` of `sitesForPolicy` vragen (reach, shift-handover, directory). Komt er een bij,
 * dan hoort hij hier; tot dan toont het scherm de keuze ook bij elk recht dat al een scope hééft,
 * zodat een bestaande scope nooit onzichtbaar wordt.
 */
const SCOPED_POLICIES = new Set([
  'reach.reminder.send',
  'reach.paper.record',
  'shift_handover.readers.view',
  'directory.person.view',
]);

type Show = 'all' | 'on' | 'off';

/**
 * Een rol bewerken, bekijken of toevoegen, op een eigen pagina (O7).
 *
 * Eén pagina voor de drie: `roleId` is `null` bij een nieuwe rol. De gegevens komen uit dezelfde
 * queries als de lijst (`['access', 'roles']`), zodat wie van de lijst komt niets opnieuw laadt.
 */
export function RoleEditorPage({ roleId }: { roleId: string | null }) {
  const { t } = useTranslation();
  const { api } = useSession();

  const roles = useQuery({
    queryKey: ['access', 'roles'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/roles');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { roles: Role[] }).roles;
    },
  });

  const policies = useQuery({
    queryKey: ['access', 'policies'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/policies');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { policies: PolicyInfo[] }).policies;
    },
  });

  const crumb = { label: t('roles.title'), to: '/admin/roles' };

  if (roles.isPending || policies.isPending) {
    return (
      <AdminPage>
        <PageHead crumb={crumb} title={t('roles.title')} />
        <Card>
          <CardPad>
            <LoadingState rows={6} label={t('common.loading')} />
          </CardPad>
        </Card>
      </AdminPage>
    );
  }

  if (roles.isError || policies.isError) {
    return (
      <AdminPage>
        <PageHead crumb={crumb} title={t('roles.title')} />
        <Card>
          <CardPad>
            <ListError
              onRetry={() => {
                void roles.refetch();
                void policies.refetch();
              }}
            />
          </CardPad>
        </Card>
      </AdminPage>
    );
  }

  const role = roleId ? (roles.data.find((item) => item.id === roleId) ?? null) : null;
  if (roleId && !role) {
    return (
      <AdminPage>
        <PageHead crumb={crumb} title={t('roles.title')} />
        <Card>
          <Empty
            icon={<AdminIcon name="key" size={24} />}
            title={t('beheer.organisatie.roles.notFound')}
          />
        </Card>
      </AdminPage>
    );
  }

  // Een sleutel per rol: de begintoestand van het formulier hoort bij déze rol.
  return <RoleEditor key={role?.id ?? 'new'} role={role} policies={policies.data} />;
}

function RoleEditor({ role, policies }: { role: Role | null; policies: PolicyInfo[] }) {
  const { t, i18n } = useTranslation();
  const { api, has, refreshPermissions } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const language = i18n.language.slice(0, 2);

  // Bekijken in plaats van bewerken: zonder het recht, en altijd bij de rol Beheerder - anders
  // kan een beheerder zichzelf buitensluiten.
  const readOnly = !has('access.role.manage') || role?.key === 'admin';
  const naamVast = role?.isSystem ?? false;

  const [name, setName] = useState<Record<string, string>>(
    (role?.name as Record<string, string> | undefined) ?? {},
  );
  const [selected, setSelected] = useState<string[]>(role?.policyKeys ?? []);
  const [scopes, setScopes] = useState<Record<string, PolicyScope>>(role?.policyScopes ?? {});
  const [search, setSearch] = useState('');
  const [show, setShow] = useState<Show>('all');
  const [open, setOpen] = useState<Set<string>>(new Set());

  const text = (value: Record<string, string | undefined>): string =>
    localized(value) ?? Object.values(value).find(Boolean) ?? '';

  const heeftBereik = (key: string) =>
    SCOPED_POLICIES.has(key) || key in (role?.policyScopes ?? {});

  /** Per onderdeel alle rechten, gesorteerd op de getoonde naam van het onderdeel. */
  const modules = useMemo(() => {
    const byModule = new Map<string, PolicyInfo[]>();
    for (const policy of policies) {
      byModule.set(policy.module, [...(byModule.get(policy.module) ?? []), policy]);
    }
    // Sorteren op de **getoonde** naam, niet op de sleutel: anders staat "Medewerkers" tussen
    // "Import" en "Meldingen" omdat het toevallig `directory` heet.
    return [...byModule.entries()].sort(([, a], [, b]) =>
      text(a[0]?.moduleLabel ?? {}).localeCompare(text(b[0]?.moduleLabel ?? {})),
    );
    // `text` hangt aan de taal; die zit al in de afhankelijkheden via `language`.
  }, [policies, language]);

  const term = search.trim().toLowerCase();
  const past = (policy: PolicyInfo) =>
    (term === '' ||
      policy.key.toLowerCase().includes(term) ||
      text(policy.label).toLowerCase().includes(term) ||
      text(policy.description).toLowerCase().includes(term)) &&
    (show === 'all' || (show === 'on') === selected.includes(policy.key));

  const zichtbaar = modules
    .map(([module, items]) => ({ module, items, matching: items.filter(past) }))
    .filter((group) => group.matching.length > 0);
  // Wie zoekt of filtert, wil de treffers zien en niet eerst elk onderdeel openklappen.
  const filtert = term !== '' || show !== 'all';
  const allesOpen = modules.length > 0 && modules.every(([module]) => open.has(module));

  const toggle = (key: string, on: boolean) => {
    setSelected((current) =>
      on
        ? [...current.filter((item) => item !== key), key]
        : current.filter((item) => item !== key),
    );
  };

  const save = useMutation({
    mutationFn: async () => {
      const policyKeys = selected;
      // Alleen wat afwijkt van "heel het bedrijf", en alleen voor rechten die de rol heeft: de
      // server weigert een bereik zonder het recht zelf.
      const policyScopes = Object.fromEntries(
        Object.entries(scopes).filter(
          ([key, scope]) => scope !== 'tenant' && policyKeys.includes(key),
        ),
      );
      const naam = Object.fromEntries(
        Object.entries(name)
          .map(([taal, waarde]) => [taal, waarde.trim()])
          .filter(([, waarde]) => waarde !== ''),
      );
      if (role) {
        const { error } = await api.PATCH('/api/v1/roles/{roleId}', {
          params: { path: { roleId: role.id } },
          // Een systeemrol houdt zijn naam; de server weigert hem ook (punt 7).
          body: { policyKeys, policyScopes, ...(naamVast ? {} : { name: naam }) } as never,
        });
        if (error) throw error;
        return;
      }
      const { error } = await api.POST('/api/v1/roles', {
        body: { name: naam, policyKeys, policyScopes } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      /*
        De melding van `@up2d8/ui` en niet die van de kit: we gaan terug naar de lijst, en de
        melding van de kit hoort bij de pagina die ze toont.
      */
      toast.show({ title: role ? t('common.saved') : t('roles.created'), tone: 'success' });
      await queryClient.invalidateQueries({ queryKey: ['access'] });
      // Je kunt je eigen rol wijzigen: dan klopt de navigatie meteen.
      await refreshPermissions();
      void navigate({ to: '/admin/roles' });
    },
    onError: (error) =>
      toast.show({
        tone: 'danger',
        title: (error as { detail?: string }).detail ?? t('roles.saveFailed'),
      }),
  });

  const naamNl = (name.nl ?? '').trim();
  const kanBewaren = naamVast || naamNl.length >= 2;
  const titelNaam = role ? roleName(role, language) : '';
  const title = !role
    ? t('roles.new')
    : readOnly
      ? t('beheer.organisatie.roles.titleView', { name: titelNaam })
      : t('beheer.organisatie.roles.titleEdit', { name: titelNaam });
  const sub = [
    role?.isSystem ? t('roles.system') : null,
    t('beheer.organisatie.roles.rightsOn', { on: selected.length, total: policies.length }),
    role ? t('beheer.organisatie.roles.membersCount', { count: role.memberCount }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <AdminPage>
      <PageHead
        crumb={{ label: t('roles.title'), to: '/admin/roles' }}
        title={title}
        sub={sub}
        actions={
          readOnly ? null : (
            <>
              <Btn variant="sec" onClick={() => void navigate({ to: '/admin/roles' })}>
                {t('common.cancel')}
              </Btn>
              <Btn
                testId="role-save"
                disabled={save.isPending || !kanBewaren}
                onClick={() => save.mutate()}
              >
                {role ? t('beheer.organisatie.roles.save') : t('roles.new')}
              </Btn>
            </>
          )
        }
      />

      <Grid variant="side">
        <Stack>
          <Card label={t('roles.rights')}>
            <Toolbar>
              <SearchField label={t('roles.searchRights')} value={search} onChange={setSearch} />
              <FilterMenu
                label={t('beheer.organisatie.roles.show')}
                value={show}
                onChange={setShow}
                alwaysShowValue
                testId="role-show-filter"
                options={[
                  { value: 'all', label: t('beheer.organisatie.roles.showAll') },
                  { value: 'on', label: t('beheer.organisatie.roles.showOn') },
                  { value: 'off', label: t('beheer.organisatie.roles.showOff') },
                ]}
              />
              <button
                type="button"
                className="a-link"
                style={{ marginLeft: 'auto' }}
                onClick={() =>
                  setOpen(allesOpen ? new Set() : new Set(modules.map(([module]) => module)))
                }
              >
                {allesOpen
                  ? t('beheer.organisatie.roles.collapseAll')
                  : t('beheer.organisatie.roles.expandAll')}
              </button>
            </Toolbar>

            {zichtbaar.length === 0 ? (
              <Empty
                icon={<AdminIcon name="search" size={24} />}
                title={t('beheer.organisatie.roles.noRights')}
                text={t('beheer.organisatie.noMatchHint')}
              />
            ) : (
              zichtbaar.map(({ module, items, matching }) => {
                const uitgeklapt = filtert || open.has(module);
                const aan = items.filter((policy) => selected.includes(policy.key)).length;
                const panelId = `rechten-${module}`;
                return (
                  <div key={module}>
                    {/*
                      De leesbare naam van het onderdeel (punt 7), met "x van y aan".

                      Er stond `directory` en `reach`: technische namen die een HR-medewerker niets
                      zeggen. De naam komt van de module zelf, niet uit een lijst in de client.
                    */}
                    <button
                      type="button"
                      className="a-item"
                      aria-expanded={uitgeklapt}
                      aria-controls={panelId}
                      data-testid="policy-module"
                      onClick={() =>
                        setOpen((huidig) => {
                          const volgende = new Set(huidig);
                          if (volgende.has(module)) volgende.delete(module);
                          else volgende.add(module);
                          return volgende;
                        })
                      }
                    >
                      <div className="a-item-info">
                        <div className="a-name">{text(items[0]?.moduleLabel ?? {}) || module}</div>
                      </div>
                      <span className="a-meta">
                        {t('beheer.organisatie.roles.moduleCount', {
                          on: aan,
                          total: items.length,
                        })}
                      </span>
                      {uitgeklapt ? (
                        <AdminIcon name="chevronUp" />
                      ) : (
                        <AdminIcon name="chevronDown" />
                      )}
                    </button>
                    {uitgeklapt ? (
                      <div className="org-rights" id={panelId}>
                        {matching.map((policy) => (
                          <PolicyRow
                            key={policy.key}
                            policyKey={policy.key}
                            label={text(policy.label)}
                            description={text(policy.description)}
                            on={selected.includes(policy.key)}
                            readOnly={readOnly}
                            scope={
                              heeftBereik(policy.key) ? (scopes[policy.key] ?? 'tenant') : null
                            }
                            onToggle={(on) => toggle(policy.key, on)}
                            onScope={(scope) =>
                              setScopes((huidig) => ({ ...huidig, [policy.key]: scope }))
                            }
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </Card>
        </Stack>

        <Stack>
          <Card>
            <CardHead title={t('beheer.organisatie.roles.nameCard')} />
            <CardPad>
              {/*
                Drie talen, want een rolnaam staat in de medewerkerslijst van elke taalgroep; nl is
                verplicht. Bij een systeemrol staan ze er alleen lezend: hernoemen kan niet (punt 7).
              */}
              {(
                [
                  ['nl', t('beheer.organisatie.fields.nameNl')],
                  ['fr', t('beheer.organisatie.fields.nameFr')],
                  ['en', t('beheer.organisatie.fields.nameEn')],
                ] as const
              ).map(([taal, label]) => (
                <Field key={taal} label={label} optional={!naamVast && taal !== 'nl'}>
                  {(props) => (
                    <Input
                      {...props}
                      data-testid={`role-name-${taal}`}
                      value={name[taal] ?? ''}
                      readOnly={readOnly || naamVast}
                      onChange={(event) => setName({ ...name, [taal]: event.target.value })}
                    />
                  )}
                </Field>
              ))}
              {naamVast && !readOnly ? (
                <p className="a-hint">{t('beheer.organisatie.roles.nameFixed')}</p>
              ) : null}
            </CardPad>
          </Card>
          <Card>
            <CardPad>
              <Note tone="info">
                {role?.key === 'admin'
                  ? t('roles.adminFixed')
                  : !role
                    ? t('beheer.organisatie.roles.newNote')
                    : role.isSystem
                      ? t('beheer.organisatie.roles.systemNote', { count: role.memberCount })
                      : t('beheer.organisatie.roles.ownNote', { count: role.memberCount })}
              </Note>
            </CardPad>
          </Card>
        </Stack>
      </Grid>
    </AdminPage>
  );
}

/**
 * Eén recht: naam, uitleg en de schakelaar (`.a-switch`), en als het recht een bereik heeft en
 * aan staat, het segment Heel het bedrijf / Eigen team / Beheerde groepen / Vestiging.
 */
function PolicyRow({
  policyKey,
  label,
  description,
  on,
  readOnly,
  scope,
  onToggle,
  onScope,
}: {
  policyKey: string;
  label: string;
  description: string;
  on: boolean;
  readOnly: boolean;
  /** `null`: dit recht heeft geen bereik. */
  scope: PolicyScope | null;
  onToggle: (on: boolean) => void;
  onScope: (scope: PolicyScope) => void;
}) {
  const { t } = useTranslation();
  const id = `recht-${policyKey.replace(/\W+/g, '-')}`;
  return (
    <div className="a-switch">
      <div className="a-switch-text">
        <div className="a-switch-title" id={`${id}-titel`}>
          {label}
        </div>
        {description ? (
          <div className="a-hint" id={`${id}-uitleg`}>
            {description}
          </div>
        ) : null}
        {scope && on ? (
          <div style={{ marginTop: 6 }}>
            <Segmented
              label={t('beheer.organisatie.roles.scopeLabel', { name: label })}
              value={scope}
              onChange={onScope}
              disabled={readOnly}
              options={SCOPES.map((value) => ({
                value,
                label: t(`beheer.organisatie.roles.scope.${value}`),
              }))}
            />
          </div>
        ) : null}
      </div>
      <Toggle
        checked={on}
        onChange={onToggle}
        labelledBy={`${id}-titel`}
        describedBy={description ? `${id}-uitleg` : undefined}
        disabled={readOnly}
        testId="policy-toggle"
      />
    </div>
  );
}
